import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { AgGridReact } from 'ag-grid-react';
import { BooleanStatus, Activity } from 'components/AgGridOptions';
import MultiIcon from '../MultiIcon';
import ConditionalBtnOrTextRenderer from 'components/ConditionalBtnOrTextRenderer';
import DownloadBtn from 'components/DownloadBtn';
import MediaButton from '../Details/MediaButton/index';
import {
  StartTimeCellRenderer,
  FinishTimeCellRenderer,
} from '../../utils/DataTransform/Events/eventTransformHelper';
import { useTranslation } from 'react-i18next';
import ConvertTextToLanguageRenderer from './ConvertTextToLanguageRenderer';
import rowEditing from 'components/RowEditing/rowEditing';
import { useSelector } from 'react-redux';
import {
  getHiddenFieldBasedOnFilters,
  getHiddenFieldIfNotEnabled,
} from 'utils/helper/product';
import ReactPaginate from 'react-paginate';
import styles from './index.module.scss';
import { getSelectedTheme } from 'selectors/themeSelector';
import cn from 'classnames';
import { getSecurityCompanyFeatures } from '../../reducers/securityCompany';
import { makeStyles } from '@material-ui/core/styles';
import { areGlobalDepartmentRecordsEmpty } from 'selectors/globalSelectors';
import CenterNumberRenderer from './CenterNumberRenderer';

import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham-dark.css';

const defaultColumnDef = {
  width: 150,
  resizable: true,
  sortable: false,
};

const useStyles = makeStyles(theme => ({
  agGrid: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    '& .ag-body-horizontal-scroll-viewport::-webkit-scrollbar': {
      width: '0.7em',
      height: '1em',
    },
    '& .ag-body-horizontal-scroll-viewport::-webkit-scrollbar-thumb': {
      borderRadius: '10px',
      backgroundColor: theme.palette.primary.main,
    },
    '& .ag-body-viewport::-webkit-scrollbar': {
      width: '1em',
    },
    '& .ag-body-viewport::-webkit-scrollbar-thumb': {
      borderRadius: '10px',
      backgroundColor: theme.palette.primary.main,
    },
    '& .ag-root': {
      border: 0,
    },
    '& .ag-header': {
      background: `${theme.palette.primary.main} !important`,
    },
    '& .ag-row': {
      width: '100%',
    },
    '& .ag-row-even': {
      background: theme.palette.background.paper,
      textAlign: 'left',
    },

    '& .ag-row-selected': {
      background: `${theme.palette.primary.main} !important`, // This is required because sometimes the row is not highlighted due to its color clash with .ag-row-even and .ag-row-odd
      borderColor: theme.palette.primary.light,
      color: theme.palette.secondary.light,
    },
    '& .ag-row-odd': {
      background: theme.palette.background.default,
    },
    '& .ag-row-hover': {
      background: theme.palette.primary.light,
    },
    '& .ag-header-row': {
      background: theme.palette.primary.main,
    },
    '& .ag-header-cell': {
      color: theme.palette.secondary.light,
      marginLeft: '5px',
    },
    '& .ag-header-column-separator-display': {
      display: 'block',
    },
    '& .ag-cell': {
      overflow: 'hidden',
      position: 'absolute',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      display: 'flex',
      alignItems: 'flex-start',
      marginLeft: '5px',
    },
    '& .ag-react-container': {
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      width: 'inherit',
      // justifyContent: 'center',
    },
  },
  pagination: {
    background: theme.palette.background.default,
    display: 'flex',
    justifyContent: 'flex-end',
    listStyle: 'none',
    cursor: 'pointer',
    alignItems: 'center',
    // borderTop: '1px solid #424242',
    height: '32px',
    padding: '0 12px',
    '& a': {
      color: theme.palette.text.secondary,
    },
  },
  paginationLinkActive: {
    padding: '5px',
    textAlign: 'center',
    background: theme.palette.primary.main,
    '& a': {
      padding: '5px',
      color: `${theme.palette.secondary.light} !important`,
    },
  },
}));

const ListView = ({
  columns = [],
  rowData,
  onSelect,
  setApi,
  paginationPageSize,
  fetchData,
  totalEntries,
  forceRefresh,
  filterKey,
  setGridAPI,
  fitColumnsToSize,
  isFilterReset,
  setFilterReset,
  showPaginate,
  filters,
}) => {
  const subscribedFeatures = useSelector(state =>
    getSecurityCompanyFeatures(state),
  );
  const areDepartmentsAvailable = useSelector(state =>
    areGlobalDepartmentRecordsEmpty(state),
  );

  const classes = useStyles();
  const themeMode = useSelector(state => getSelectedTheme(state));
  // checks if a column field in Aggrid need to be hidden depending on a feature
  const isDepartmentsEnabled = getHiddenFieldIfNotEnabled({
    subscribedFeatures,
    feature: 'DEPARTMENTS',
    field: 'departments',
    areDepartmentsAvailable,
  });

  const isArchivedEnabled = getHiddenFieldBasedOnFilters({
    filters,
    filterValueToFind: 'archived',
    field: 'archived',
  });

  // add the columns that needs to be programatically hidden
  const hiddenColumns = [isDepartmentsEnabled, isArchivedEnabled];

  const { t } = useTranslation();

  const [currentPage, setCurrentPage] = useState(0);
  const [from, setFrom] = useState(0);
  const [pageCount, setPageCount] = useState();

  useEffect(() => {
    typeof fetchData === 'function' && fetchData(from, paginationPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from]);

  function handlePageClick({ selected }) {
    const pageNumber = selected === 0 ? 0 : `${selected}00`;
    setFrom(parseInt(pageNumber, 10) / 2);
    setCurrentPage(selected);
  }
  useEffect(() => {
    const pageCount = Math.ceil(totalEntries / paginationPageSize);

    if (pageCount === 1) {
      setCurrentPage(0);
    }
    if (isFilterReset) {
      // go to the first page and re-fetch all
      handlePageClick({ selected: 0 });
      setFrom(0);
      setFilterReset(false);
      fetchData(from / 2, paginationPageSize);
      return;
    }

    // if the filter is applied
    if (filterKey) {
      // data from the api is the source of truth
      setPageCount(pageCount);
    } else {
      setPageCount(pageCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowData, totalEntries, isFilterReset, filterKey, currentPage]);

  const onGridReady = params => {
    const gridApi = params.api;
    if (setApi) setApi(gridApi);
    if (setGridAPI) setGridAPI(gridApi);
    fitColumnsToSize && gridApi.sizeColumnsToFit();
  };

  const translatedColumns = columns =>
    columns?.map(m => {
      //check if hidden column field matches the field name in the column and hide field should be true
      const requiredHiddenField = hiddenColumns.find(
        x => m.field === x.field && x.hide,
      );
      return {
        ...m,
        headerName: t(m.headerName),
        ...(requiredHiddenField && { hide: requiredHiddenField.hide }),
      };
    });
  //This function will convert the internals of AgGrid like pagination etc
  const localeTextFunct = (_, value) => {
    /**
     * TODO: Tried to match the value to json file. For future make this equivalent to the value from AgGrid
     */

    if (value === 'No Rows To Show') {
      return t('No Rows to Show');
    }
    return t(value);
  };
  useEffect(() => {
    if (forceRefresh) {
      typeof fetchData === 'function' && fetchData(from, paginationPageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, forceRefresh]);

  const pageCountTemplate = ` ${paginationPageSize * currentPage + 1} ${t(
    'to',
  )} ${
    pageCount === currentPage + 1 // if it is last page then end count is totalEntries
      ? totalEntries
      : rowData.length * (currentPage + 1)
  } ${t('of')} ${totalEntries} `;

  return (
    <>
      <div
        className={cn('ag-theme-balham-dark', {
          [classes.agGrid]: themeMode,
        })}
        style={{ flex: 1 }} // NOTE: clean-up with MUI-card 100%
      >
        <AgGridReact
          localeTextFunc={localeTextFunct}
          onGridReady={onGridReady}
          columnDefs={translatedColumns(columns)}
          defaultColDef={defaultColumnDef}
          onRowSelected={onSelect}
          deltaRowDataMode={true}
          maxBlocksInCache={1}
          rowSelection={'single'}
          rowHeight={30}
          headerHeight={35}
          rowData={rowData}
          colResizeDefault="shift"
          suppressCellSelection={true}
          getRowNodeId={data => data?.id}
          frameworkComponents={{
            iconComponent: MultiIcon,
            centredNumber: CenterNumberRenderer,
            statusComponent: BooleanStatus,
            activityComponent: Activity,
            conditionalBtnOrTextComponent: ConditionalBtnOrTextRenderer,
            downloadBtn: DownloadBtn,
            audioComponent: MediaButton,
            roleArray: ConditionalBtnOrTextRenderer,
            convertStartTime: StartTimeCellRenderer,
            convertFinishTime: FinishTimeCellRenderer,
            convertPublishStatus: ConvertTextToLanguageRenderer,
            convertDistribution: ConvertTextToLanguageRenderer,
            convertEventStatus: ConvertTextToLanguageRenderer, //overridden to convert all the things.
            convertEventCategory: ConvertTextToLanguageRenderer,
            convertEventType: ConvertTextToLanguageRenderer,
            rowEditingBtn: rowEditing,
          }}
          onCellFocused={e => {
            if (e.column && e.column.colDef.cellRenderer === 'downloadBtn') {
              e.api.gridOptionsWrapper.gridOptions.suppressRowClickSelection = true;
            } else {
              e.api.gridOptionsWrapper.gridOptions.suppressRowClickSelection = false;
            }
          }}
        />
      </div>
      {showPaginate && (
        <ReactPaginate
          previousLabel={
            rowData.length > 0 ? `${pageCountTemplate} < ` : pageCountTemplate
          }
          nextLabel={rowData.length > 0 ? ' > ' : ''}
          marginPagesDisplayed={2}
          pageRangeDisplayed={3}
          pageCount={isNaN(pageCount) ? 1 : pageCount}
          forcePage={currentPage}
          onPageChange={handlePageClick}
          containerClassName={classes.pagination}
          previousLinkClassName={styles.pagination__link__prev}
          nextLinkClassName={styles.pagination__link}
          disabledClassName={styles.pagination__link__disabled}
          activeClassName={classes.paginationLinkActive}
        />
      )}
    </>
  );
};

ListView.defaultProps = {
  showPaginate: true,
  paginationPageSize: 50,
};

ListView.propTypes = {
  columns: PropTypes.array.isRequired,
  rowData: PropTypes.array.isRequired,
  setApi: PropTypes.func,
  onSelect: PropTypes.func,
  pagination: PropTypes.bool,
  fetchData: PropTypes.func,
  rowModelType: PropTypes.oneOf(['infinite']),
  totalEntries: PropTypes.number,
  agGridApi: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  filterKey: PropTypes.bool,
  forceRefresh: PropTypes.bool,
  isFilterReset: PropTypes.bool,
  setFilterReset: PropTypes.func,
  setGridAPI: PropTypes.func,
  fitColumnsToSize: PropTypes.bool,
  paginationPageSize: PropTypes.number,
  paginationAutoPageSize: PropTypes.bool,
  showPaginate: PropTypes.bool,
  filters: PropTypes.object,
};

export default ListView;
